import { useCallBackendApi, useRedisWritesBlocked } from '@/apis/call';
import { queryClient } from '@/App';
import { walkTreeAndRemoveEphemeralIds } from '@/util';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { Typography } from '@mui/material';
import { searchTemplateDataAtom } from '@/state/searchTemplate/searchTemplateData';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { isSearchTreeNode, Search, SearchMetadata } from './SaveSearchDialog';

export type UpdateSearchDialogProps = {
  isOpen: boolean;
  onClose: (saved: boolean) => void;
  openSaveSearchDialog: ({ prefill }: { prefill: boolean }) => void;
  search: Search;
  searchMetadata: SearchMetadata;
};

const useUpdateSearch = () => {
  const updateStatus = useCallBackendApi<object, boolean>();
  const { enqueueSuccessNotification } = useSaladBar();
  return useMutation({
    mutationFn: async ({ metadata, search }: { search: Search; metadata: SearchMetadata }) => {
      return updateStatus({
        endpoint: `/searches/${metadata.id}`,
        method: 'put',
        data: { search: JSON.stringify(search) },
      });
    },
    onSuccess: (result, { metadata: { name, id } }) => {
      enqueueSuccessNotification(`Search ${name} successfully updated`);
      queryClient.invalidateQueries({ queryKey: ['searches'] });
      queryClient.invalidateQueries({ queryKey: ['search', id] });
    },
  });
};

const UpdateSearchDialog = ({
  isOpen,
  onClose,
  openSaveSearchDialog,
  search,
  searchMetadata,
}: UpdateSearchDialogProps) => {
  const { mutateAsync: updateSearch, isPending } = useUpdateSearch();
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const setSearchTemplateData = useSetAtom(searchTemplateDataAtom);
  return (
    <AgDialog
      isOpen={isOpen}
      onClose={() => onClose(false)}
      dialogTitle="Update or save as new?"
      primaryButton={{
        text: 'Save new',
        onClick: async () => {
          if (writesBlocked) return;
          openSaveSearchDialog({ prefill: false });
          onClose(false);
        },
        disabled: isPending || writesBlocked,
      }}
      additionalButtons={[
        {
          text: 'Update',
          onClick: async () => {
            if (writesBlocked) return;
            try {
              // Strip out ephemeral ids (_leafNodeId, _internalNodeId) from the search tree if needed
              if (isSearchTreeNode(search)) {
                // eslint-disable-next-line react-hooks/immutability -- drop ephemeral ids before the update payload
                search = walkTreeAndRemoveEphemeralIds(search);
                await updateSearch({
                  search: walkTreeAndRemoveEphemeralIds(search),
                  metadata: searchMetadata,
                });
              } else {
                await updateSearch({ search, metadata: searchMetadata });
              }
              setSearchTemplateData(undefined);
              onClose(true);
            } catch {
              enqueueErrorNotification('The search could not be updated.');
            }
          },
          disabled: isPending || writesBlocked,
        },
      ]}
      secondaryButton={{ text: 'Cancel', onClick: async () => onClose(false), disabled: isPending }}
      data-testid="SaveSearchDialog"
    >
      <Typography>
        You have made changes to an existing saved search. Do you want to update your existing saved
        search or save a new saved search?
      </Typography>
    </AgDialog>
  );
};

export default UpdateSearchDialog;
